import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CatalogFilter, CatalogProductRow, CatalogSort } from '../domain/catalogTypes';
import { CATALOG_ADMIN_PAGE_SIZE_STORAGE_KEY, type CatalogListPageSize } from '../domain/catalogPaginationConstants';
import { preflightCatalogSnapshotForRead } from '../infra/catalogReadPreflight';
import { countCatalogProducts, listCatalogProducts } from '../infra/catalogReadRepo';
import type { CatalogItemTypeFilter } from '../domain/productTypes';
import type { Product } from '../domain/productTypes';
import { useScreenRefresh } from '../../shared/ui/hooks-screen/useScreenRefresh';
import { usePaginatedRemoteList } from '../../shared/data/usePaginatedRemoteList';
import { subscribeCatalogSynced } from '../../sync/core/catalogSyncListeners';

export { CATALOG_ADMIN_PAGE_SIZE_STORAGE_KEY, type CatalogListPageSize };

type CatalogListQuery = {
  search: string;
  pinnedProductId: string | null;
  status: CatalogFilter;
  category?: Product['categoryId'];
  itemType: CatalogItemTypeFilter;
  sortBy: CatalogSort;
  brandId: string | null;
  supplierId: string | null;
  alertFilter: boolean;
  incompleteFilter: boolean;
};

type Params = {
  searchQuery: string;
  pinnedProductId?: string | null;
  statusFilter: CatalogFilter;
  categoryFilter: Product['categoryId'] | 'all';
  itemTypeFilter: CatalogItemTypeFilter;
  brandFilter?: string | null;
  supplierFilter?: string | null;
  sortBy: CatalogSort;
  alertFilter: boolean;
  incompleteFilter: boolean;
  /** Sucursal activa — stock/alertas dependen de branch; sin esto el listado no refresca al cambiar. */
  branchId?: string | null;
  refreshCategories: () => Promise<void>;
};

/** Catálogo administrativo: páginas discretas (5 o 10 ítems). */
export function useCatalogListController({
  searchQuery,
  pinnedProductId = null,
  statusFilter,
  categoryFilter,
  itemTypeFilter,
  brandFilter = null,
  supplierFilter = null,
  sortBy,
  alertFilter,
  incompleteFilter,
  branchId = null,
  refreshCategories,
}: Params) {
  const didNetworkPreflightRef = useRef(false);
  const hasLoadedCatalogOnceRef = useRef(false);
  const [catalogCriteriaRefreshing, setCatalogCriteriaRefreshing] = useState(false);

  const criteriaKey = useMemo(
    () =>
      `${branchId ?? ''}|${searchQuery}|${pinnedProductId ?? ''}|${statusFilter}|${categoryFilter}|${itemTypeFilter}|${brandFilter ?? ''}|${supplierFilter ?? ''}|${sortBy}|${alertFilter}|${incompleteFilter}`,
    [
      branchId,
      searchQuery,
      pinnedProductId,
      statusFilter,
      categoryFilter,
      itemTypeFilter,
      brandFilter,
      supplierFilter,
      sortBy,
      alertFilter,
      incompleteFilter,
    ],
  );

  const listQuery = useMemo<CatalogListQuery>(
    () => ({
      search: pinnedProductId ? '' : searchQuery,
      pinnedProductId: pinnedProductId ?? null,
      status: statusFilter,
      category: categoryFilter === 'all' ? undefined : categoryFilter,
      itemType: itemTypeFilter,
      sortBy,
      brandId: brandFilter,
      supplierId: supplierFilter,
      alertFilter,
      incompleteFilter,
    }),
    [
      brandFilter,
      categoryFilter,
      itemTypeFilter,
      pinnedProductId,
      searchQuery,
      sortBy,
      statusFilter,
      supplierFilter,
      alertFilter,
      incompleteFilter,
    ],
  );

  const fetchCatalogPage = useCallback(
    async ({ limit, offset, query }: { limit: number; offset: number; query: CatalogListQuery }) => {
      if (!didNetworkPreflightRef.current) {
        await preflightCatalogSnapshotForRead();
        didNetworkPreflightRef.current = true;
      }
      return listCatalogProducts({
        search: query.search,
        status: query.status,
        category: query.category ?? undefined,
        itemType: query.itemType,
        sortBy: query.sortBy,
        limit,
        offset,
        pinnedProductId: query.pinnedProductId,
        brandId: query.brandId ?? undefined,
        supplierId: query.supplierId ?? undefined,
        alertFilter: query.alertFilter,
        incompleteFilter: query.incompleteFilter,
      });
    },
    [],
  );

  const countCatalog = useCallback(
    (query: CatalogListQuery) =>
      countCatalogProducts({
        search: query.pinnedProductId ? '' : query.search,
        status: query.status,
        category: query.category ?? undefined,
        itemType: query.itemType,
        pinnedProductId: query.pinnedProductId,
        brandId: query.brandId ?? undefined,
        supplierId: query.supplierId ?? undefined,
        alertFilter: query.alertFilter,
        incompleteFilter: query.incompleteFilter,
      }),
    [],
  );

  const paging = usePaginatedRemoteList<CatalogProductRow, CatalogListQuery>({
    storageKey: CATALOG_ADMIN_PAGE_SIZE_STORAGE_KEY,
    criteriaKey,
    query: listQuery,
    logLabel: 'Catalog',
    fetchPage: fetchCatalogPage,
    count: countCatalog,
  });

  useEffect(() => {
    if (paging.loading) {
      setCatalogCriteriaRefreshing(hasLoadedCatalogOnceRef.current);
      return;
    }
    if (paging.items.length > 0 || paging.totalCount === 0) {
      hasLoadedCatalogOnceRef.current = true;
    }
    setCatalogCriteriaRefreshing(false);
  }, [paging.items.length, paging.loading, paging.totalCount]);

  const refreshCatalogTotalCount = paging.refreshTotalCount;

  const loadFirstPage = useCallback(async () => {
    paging.resetToFirstPage();
    await refreshCatalogTotalCount();
    await paging.load();
  }, [paging, refreshCatalogTotalCount]);

  const reloadCatalogPage = useCallback(async () => {
    await Promise.all([refreshCategories(), refreshCatalogTotalCount()]);
    paging.resetToFirstPage();
    await paging.load();
  }, [paging, refreshCatalogTotalCount, refreshCategories]);

  const { isRefreshing, onRefresh: handleRefresh, runRefresh } = useScreenRefresh(async () => {
    await preflightCatalogSnapshotForRead({ force: true });
    didNetworkPreflightRef.current = true;
    await reloadCatalogPage();
  });

  /** Tras import CSV: recarga la lista sin pull remoto que pise cambios locales pendientes de sync. */
  const handleRefreshAfterLocalEdit = useCallback(async () => {
    await runRefresh({ feedback: false, reload: reloadCatalogPage });
  }, [reloadCatalogPage, runRefresh]);

  // Stock en filas viene del snapshot RAM; tras produce/ajuste hay que releer sin F5.
  useEffect(() => {
    return subscribeCatalogSynced(() => {
      void handleRefreshAfterLocalEdit();
    });
  }, [handleRefreshAfterLocalEdit]);

  return {
    catalogListRef: paging.flatListRef,
    catalogWideScrollRef: paging.scrollViewRef,
    products: paging.items,
    catalogPageIndex: paging.pageIndex,
    catalogTotalCount: paging.totalCount,
    pageSize: paging.pageSize,
    setCatalogPageSize: paging.setPageSize,
    hasMore: paging.hasMore,
    catalogLoading: paging.loading,
    /** Error del último fetch — la UI debe distinguir error de lista vacía. */
    catalogError: paging.error,
    catalogCriteriaRefreshing,
    isRefreshing,
    loadFirstPage,
    handleRefresh,
    handleRefreshAfterLocalEdit,
    loadCatalogNextPage: paging.loadNextPage,
    loadCatalogPreviousPage: paging.loadPreviousPage,
    goToCatalogPage: paging.goToPage,
    refreshCatalogTotalCount,
  };
};
